import React, { FC, useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import {
  catchError,
  concatMap,
  EMPTY,
  finalize,
  from,
  of,
  Subscription,
  switchMap,
} from 'rxjs'

import { Grid, Link, Typography } from '@mui/material'
import { LinkButton } from '@procom-labs/atoms'
import {
  AuthProfileType,
  CreateContactPayload,
  getClientCode,
  getClientPortalHost,
  getVendorCode,
  gtmEvents,
  IAccountType,
  LoginTypes,
  RecaptchaActions,
  useMfaStore,
  useSubjectSelector,
  useSubscriptionRef,
} from '@procom-labs/common'
import { useAlert } from '@procom-labs/molecules'

import { BaseLayout } from '@auth-portal/components/base-layout'
import { Header } from '@auth-portal/components/header'
import {
  PasswordCreateScreen,
  PasswordValidateScreen,
} from '@auth-portal/components/password'
import { VerifyOtpLogin } from '@auth-portal/components/verify-otp-login'
import {
  useAuthService,
  useIsUserCreationAllowed,
  useRecaptcha,
  useTrackingWrapper,
} from '@auth-portal/hooks'
import { authStore } from '@auth-portal/store'
import {
  AwsUserStatusTypes,
  ClientUserCreateResponse,
  FlowRequestType,
  UserExistResponse,
  ValidateSignInFormValues,
} from '@auth-portal/types'
import { getCallbackURL, getOriginBaseUrl } from '@auth-portal/util'

export const ValidateSignIn: FC<{}> = () => {
  const { addAlert } = useAlert()
  const navigate = useNavigate()
  const authService = useAuthService()
  const mfaStore = useMfaStore()
  const location = useLocation()
  const { getRecaptchaToken } = useRecaptcha()
  const [searchParams] = useSearchParams()
  const { t, i18n } = useTranslation('main')

  const userResponse = location.state as UserExistResponse
  const profileType = userResponse?.profileType ?? ''
  const isContractor = profileType === LoginTypes.Contractor.toLowerCase()
  const isGorilla = profileType === LoginTypes.GorillaResume.toLowerCase()
  const isClient = profileType === LoginTypes.Client.toLowerCase()

  const isUserCreationAllowed = useIsUserCreationAllowed()
  const { track } = useTrackingWrapper()

  const { isMfaCodeRequested, siteConfig } = useSubjectSelector(authStore, [
    'isMfaCodeRequested',
    'siteConfig',
  ])

  const email = searchParams.get('email') ?? ''
  const isNewUser = (userResponse?.isNewUser ?? '') === 'true'
  const firstLogin = (userResponse?.firstLogin ?? '') === 'true'

  const [isOtpOption, setIsOtpOption] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [isCognitoUserExists, setIsCognitoUserExists] = useState(false)
  const [cognitoUserStatus, setCognitoUserStatus] = useState<string[]>([])
  const [isCognitoUserCheckLoading, setIsCognitoUserCheckLoading] =
    useState(true)
  const [isUserEmailVerified, setIsUserEmailVerified] = useState(false)

  const isCognitoUserConfirmed = cognitoUserStatus.includes(
    AwsUserStatusTypes.CONFIRMED
  )

  const isUserWithoutPassword = !isCognitoUserExists || !isCognitoUserConfirmed

  const afterLoginSubscriptionRef = useSubscriptionRef()
  const handleSubmitSubscriptionRef = useSubscriptionRef()

  const formInstance = useForm<ValidateSignInFormValues>({
    defaultValues: { email },
  })
  const { handleSubmit } = formInstance

  const getProfileType = useCallback((): AuthProfileType | string => {
    if (isContractor) return AuthProfileType.Contractor
    if (isGorilla) return AuthProfileType.GorillaResumeUser
    return ''
  }, [isContractor, isGorilla])

  const handleLoginAfterAccountCreate = useCallback(
    (password: string, response: ClientUserCreateResponse) => {
      const {
        onboardingParam,
        migrationToken,
        isFboMigrationTokenValid,
        tenantParam,
      } = userResponse
      const isFboUser = migrationToken && isFboMigrationTokenValid
      const isOnboardingInvite = onboardingParam

      afterLoginSubscriptionRef.current = authService
        .authenticate({
          password,
          username: email,
          context: 'complete-registration',
          queryParams: {
            isPendingCognitoProcess: true,
            newAccount: isNewUser ? 'true' : 'false',
            firstLogin: firstLogin ? 'true' : 'false',
          },
        })
        .pipe(
          switchMap(() =>
            from(getRecaptchaToken(RecaptchaActions.ActivateFboMapping)).pipe(
              concatMap((token) => {
                return isFboUser || isOnboardingInvite
                  ? authService.activateFboUserMapping(
                      migrationToken,
                      onboardingParam,
                      email,
                      token,
                      tenantParam
                    )
                  : of(null)
              }),
              concatMap(() => authService.setUserRegisteredOn()),
              concatMap(() =>
                authService.verifyEmail({
                  email,
                  migrationToken,
                  onboardingCode: onboardingParam ?? response.id,
                })
              )
            )
          ),
          finalize(() => setIsLoading(false)),
          catchError(() => {
            setIsLoading(false)
            return EMPTY
          })
        )
        .subscribe({
          next: () => {
            authService.redirectToClientPortal()
          },
        })
    },
    [
      email,
      isNewUser,
      firstLogin,
      userResponse,
      getRecaptchaToken,
      afterLoginSubscriptionRef,
    ]
  )

  const handleCreateAccount = useCallback(
    (data: ValidateSignInFormValues) => {
      let payload: CreateContactPayload = {
        email,
        password: data.password,
        firstName: userResponse?.firstName ?? '',
        lastName: userResponse?.lastName ?? '',
        phoneNumber: userResponse?.phoneNumber ?? '',
        recaptchaToken: '',
        requestType: FlowRequestType.signup,
        language: i18n.language,
        vendorCode: getVendorCode(),
        callbackUrl: getCallbackURL(),
        originBase: getOriginBaseUrl(),
        authPortalBase: window.location.origin,
        clientCode: getClientCode(getClientPortalHost()) ?? '',
        profileType: getProfileType(),
      }

      handleSubmitSubscriptionRef.current = from(
        getRecaptchaToken(RecaptchaActions.Signup)
      )
        .pipe(
          switchMap((token) => {
            payload = {
              ...payload,
              recaptchaToken: token,
            }
            return authService.createContactWithoutPassword(
              payload,
              isClient ? IAccountType.Client : IAccountType.Contractor // Adjust this
            )
          }),
          catchError(() => {
            setIsLoading(false)
            return EMPTY
          }),
          finalize(() => setIsLoading(false))
        )
        .subscribe({
          next: (response) => {
            handleLoginAfterAccountCreate(data.password, response)
          },
        })
    },
    [
      email,
      i18n.language,
      isClient,
      userResponse?.firstName,
      userResponse?.lastName,
      userResponse?.phoneNumber,
      getProfileType,
      getRecaptchaToken,
      handleSubmitSubscriptionRef,
      handleLoginAfterAccountCreate,
    ]
  )

  const handleSetPermanentPassword = useCallback(
    (data: ValidateSignInFormValues) => {
      const {
        onboardingParam,
        migrationToken,
        isFboMigrationTokenValid,
        tenantParam,
      } = userResponse
      const isFboUser = migrationToken && isFboMigrationTokenValid
      const isOnboardingInvite = onboardingParam

      handleSubmitSubscriptionRef.current = from(
        getRecaptchaToken(RecaptchaActions.CodeSignIn)
      )
        .pipe(
          switchMap((token) => {
            return authService
              .createPasswordWithCodeSignIn(
                data.otp,
                email,
                token,
                data.password
              )
              .pipe(
                concatMap(() =>
                  from(getRecaptchaToken(RecaptchaActions.ActivateFboMapping))
                ),
                concatMap((fboMappingToken) => {
                  return isFboUser || isOnboardingInvite
                    ? authService.activateFboUserMapping(
                        migrationToken,
                        onboardingParam,
                        email,
                        fboMappingToken,
                        tenantParam
                      )
                    : of(null)
                })
              )
          }),
          finalize(() => setIsLoading(false))
        )
        .subscribe({
          next: () => {
            authService.redirectToClientPortal({
              isOtpLogin: true,
              ...(isNewUser && { newAccount: 'true' }),
              ...(firstLogin && { firstLogin: 'true' }),
            })
          },
        })
    },
    [
      email,
      firstLogin,
      isNewUser,
      userResponse,
      getRecaptchaToken,
      handleSubmitSubscriptionRef,
    ]
  )

  const handleAuthenticate = useCallback(
    (data: ValidateSignInFormValues) => {
      handleSubmitSubscriptionRef.current = authService
        .authenticate({
          username: email,
          password: data.password,
        })
        .pipe(finalize(() => setIsLoading(false)))
        .subscribe({
          next: (response) => {
            const mfaType = response?.mfaType
            if (mfaType) mfaStore.dispatch({ mfaType })
          },
          error: (error) => {
            addAlert({
              severity: 'error',
              message: error.message,
            })
          },
          complete: () => {
            track({
              eventName: gtmEvents.FormLogin,
              submissionStatus: 'true',
              email,
            })
          },
        })
    },
    [email, addAlert, track, handleSubmitSubscriptionRef]
  )

  const onSubmit = useCallback(
    (data: ValidateSignInFormValues) => {
      setIsLoading(true)
      if (isCognitoUserExists) {
        // Case 1 : Login via Cognito Password
        if (isCognitoUserConfirmed) {
          handleAuthenticate(data)
        } else {
          // case 2: Set permanent password for this existing cognito user
          handleSetPermanentPassword(data)
        }
      } else {
        // !isCognitoUserExists
        // Case 3 : Create Cognito user and login
        handleCreateAccount(data)
      }
    },
    [
      isCognitoUserExists,
      isCognitoUserConfirmed,
      handleCreateAccount,
      handleAuthenticate,
      handleSetPermanentPassword,
    ]
  )

  useEffect(() => {
    // Redirect to mfa if login via password
    if (isMfaCodeRequested && isCognitoUserExists && isCognitoUserConfirmed)
      navigate('/login-mfa')
  }, [
    isMfaCodeRequested,
    navigate,
    isCognitoUserExists,
    isCognitoUserConfirmed,
  ])

  // UseEffect - Check if user exists in cognito
  useEffect(() => {
    const subscription = new Subscription()
    if (email) {
      subscription.add(
        from(getRecaptchaToken(RecaptchaActions.GetAnonymousUserInfo))
          .pipe(
            switchMap((token) =>
              authService.getIfUserExist(email, token, 'true')
            ),
            finalize(() => setIsCognitoUserCheckLoading(false))
          )
          .subscribe({
            next: (response) => {
              if (response) {
                setIsCognitoUserExists(true)
                setIsUserEmailVerified(response.isEmailVerified)
                const awsUserStatuses: string[] = []
                if (response.userStatus) {
                  response.userStatus.forEach((status) =>
                    awsUserStatuses.push(status)
                  )
                }
                setCognitoUserStatus(awsUserStatuses)
              }
            },
          })
      )
    }
    return () => {
      if (subscription != null && !subscription.closed) {
        subscription.unsubscribe()
      }
    }
  }, [email, getRecaptchaToken])

  // SiteConfig if not already loaded
  useEffect(() => {
    const subscription = new Subscription()
    if (!siteConfig) {
      subscription.add(
        authService.getSiteConfigurationByOriginBase().subscribe()
      )
    }
    return () => {
      if (subscription && !subscription.closed) {
        subscription.unsubscribe()
      }
    }
  }, [siteConfig])

  return (
    <BaseLayout>
      <FormProvider {...formInstance}>
        <Header />
        {isOtpOption ? (
          <>
            <VerifyOtpLogin isUserEmailVerified={isUserEmailVerified} />
            {(isUserEmailVerified ||
              (!isUserEmailVerified &&
                isUserWithoutPassword &&
                isUserCreationAllowed)) && (
              <>
                <Typography
                  component="p"
                  variant="body1"
                  mb={4.5}
                  mt={3}
                  sx={{ textDecoration: 'capital' }}
                >
                  {t('form.contractorSignup.or')}
                </Typography>

                <LinkButton
                  onClick={() => setIsOtpOption(false)}
                  underline={false}
                  sx={{
                    textOverflow: 'wrap',
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                  }}
                >
                  {!isCognitoUserCheckLoading && (
                    <Typography
                      variant="body1Bold700"
                      lineHeight={1.5}
                      component="span"
                    >
                      {isUserWithoutPassword && isUserCreationAllowed
                        ? t('form.login.password.setupPassword')
                        : t('form.login.password.loginWithPassword')}
                    </Typography>
                  )}
                </LinkButton>
              </>
            )}
          </>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            {isUserWithoutPassword && isUserCreationAllowed ? (
              <PasswordCreateScreen isLoading={isLoading} />
            ) : (
              <PasswordValidateScreen isLoading={isLoading} />
            )}

            <Typography
              component="p"
              variant="body1"
              my={4}
              sx={{ textDecoration: 'capital' }}
            >
              {t('form.contractorSignup.or')}
            </Typography>

            <Grid mx={4}>
              <LinkButton
                onClick={() => setIsOtpOption(true)}
                underline={false}
                sx={{
                  textOverflow: 'wrap',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                }}
              >
                <Typography
                  variant="body1Bold700"
                  lineHeight={1.5}
                  component="span"
                >
                  {t('form.createAccount.oneTimeCode')}
                </Typography>
              </LinkButton>
            </Grid>
          </form>
        )}
      </FormProvider>

      <Grid mt={4}>
        <Link component={RouterLink} underline="none" to="/">
          <Typography variant="body1Bold700" lineHeight={1.5} component="span">
            {t('form.reset.backLoginBtn')}
          </Typography>
        </Link>
      </Grid>
    </BaseLayout>
  )
}
